import React, { useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { BottomSheet, Box, Stack, Typography } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import allRoutes from '@/pages/corporate/routes/contents';
import { articleDetailRoute } from '@/pages/corporate/routes/contents/articles';
import { courseDetailRoute } from '@/pages/corporate/routes/contents/course';
import { pathDetailRoute } from '@/pages/corporate/routes/contents/path';
import { quizDetailRoute } from '@/pages/corporate/routes/contents/quiz';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { LanguageConfig, LanguageEnum, ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';

interface SelectLanguageMobileProps {
  placeholder?: string;
  languageConfigs: LanguageConfig[];
  languageType: LanguageEnum;
  id: string;
  resourceType: ResourceTypeEnum;
  localizedContentsMap?: Map<string, ResourceStatusEnum>;
}

const SelectLanguageMobile: React.FC<SelectLanguageMobileProps> = ({
  placeholder,
  languageConfigs,
  languageType,
  id,
  resourceType,
  localizedContentsMap,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();
  const [isOpen, setIsOpen] = useState(false);

  const allLanguagesMap = useMemo(() => {
    const allLanguagesMap: { [key: string]: string } = {
      GUJARATI: 'Gujarati',
      KANNADA: 'Kannada',
      MALAYALAM: 'Malayalam',
    };
    Object.entries(LanguageEnum).forEach(([key, value]) => (allLanguagesMap[value] = key));
    return allLanguagesMap;
  }, []);

  const options = useMemo(() => {
    if (!languageType) {
      const englisLanguageConfig = languageConfigs.find(
        (languageConfig: any) => languageConfig.languageType == LanguageEnum.English,
      );
      if (!englisLanguageConfig) {
        languageConfigs.push({ languageType: LanguageEnum.English, contentId: id });
      }
    }
    const options = languageConfigs?.map((languageConfig: any) => ({
      value: languageConfig.languageType,
      label: allLanguagesMap[languageConfig.languageType],
      contentId: languageConfig.contentId,
    }));

    return options;
  }, [languageConfigs, languageType, allLanguagesMap, id]);

  const currentLanguage = !!languageType ? languageType : LanguageEnum.English;
  const currentLabel = allLanguagesMap[currentLanguage];

  const handleSelect = (option: any) => {
    let routes = courseDetailRoute(option.contentId);
    switch (resourceType) {
      case ResourceTypeEnum.Quiz:
        if (
          localizedContentsMap?.get(option.contentId) == ResourceStatusEnum.Draft &&
          isAdminRoute
        ) {
          const quizEditor = allRoutes.QuizEditor(option.contentId);
          navigation.replace(quizEditor, { appendQuery: true });
          return;
        } else {
          routes = quizDetailRoute(option.contentId);
        }
        break;
      case ResourceTypeEnum.Article:
        routes = articleDetailRoute(option.contentId);
        break;
      case ResourceTypeEnum.Path:
        routes = pathDetailRoute(option.contentId);
    }
    const { admin, learner } = routes;
    const courseDetailsRoute = isAdminRoute ? admin : learner;
    navigation.replace(courseDetailsRoute, { appendQuery: true });
    setIsOpen(false);
  };

  return (
    <>
      <Stack
        alignItems="center"
        className={cn(
          'cursor-pointer border border-border ps-8 pe-12 xs:px-12 min-h-[4rem] rounded-8 ',
        )}
        gapX={6}
        onClick={() => setIsOpen(true)}>
        <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
          Language : {currentLabel}
        </Typography>
      </Stack>

      <BottomSheet open={isOpen} onClose={() => setIsOpen(false)} onOpen={() => setIsOpen(true)}>
        <Box className="p-16">
          <Typography className="mb-16" variant="heading-s">
            {placeholder ?? t('Common:Actions.ChooseLanguage')}
          </Typography>
          <Stack direction="col" gap={8}>
            {options.map((option) => (
              <Box
                key={option.value}
                className={cn(
                  'cursor-pointer rounded-md p-12 hover:bg-bg-hover',
                  option.value === currentLanguage && 'bg-bg-selected',
                )}
                onClick={() => handleSelect(option)}>
                <Typography variant="body-short-01">{option.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </BottomSheet>
    </>
  );
};

export default SelectLanguageMobile;
